import {
  ButtonGroupNav,
  DataObjectGroupType,
  DataObjectOutputType,
  getDayjs,
  parseGroupDescriptionRef,
  useDataObjectGroupsWithDescriptionsQuery,
} from '@atlas-frontend/sdk';

import translations from './VisualValidationNavigationButtons.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useWatch } from 'react-hook-form';
import UriTemplate from 'uri-template-lite';
import { useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { path } from '../../paths';

const { addTranslations, t } = i18nNsFactory(
  'visual-validation-navigation-buttons',
);
addTranslations(translations);

export const VisualValidationNavigationButtons = () => {
  const [groupDescriptionRef, valueDate, version] = useWatch({
    name: ['groupDescriptionRef', 'valueDate', 'version'],
  });

  const { descriptionId } = useMemo(
    () =>
      groupDescriptionRef
        ? parseGroupDescriptionRef(groupDescriptionRef)
        : { descriptionId: undefined },
    [groupDescriptionRef],
  );

  const { data: dataObjectGroupsWithDescriptions } =
    useDataObjectGroupsWithDescriptionsQuery(
      {
        groupType: DataObjectGroupType.O,
      },
      {
        placeholderData: keepPreviousData,
      },
    );

  const isReport = useMemo(() => {
    if (!dataObjectGroupsWithDescriptions || !descriptionId) {
      return false;
    }

    return Array.from(dataObjectGroupsWithDescriptions).some(
      ([id, group]) =>
        id !== null &&
        group.descriptions.some(
          (desc) =>
            desc.id === descriptionId &&
            desc.outputTypeCode === DataObjectOutputType.REPORT,
        ),
    );
  }, [dataObjectGroupsWithDescriptions, descriptionId]);

  const fullParams = useMemo(() => {
    const valueDateDayjs = getDayjs(valueDate);
    return {
      groupDescriptionRef,
      valueDate: valueDateDayjs
        ? valueDateDayjs.format('YYYY-MM-DD')
        : undefined,
      version,
    };
  }, [groupDescriptionRef, valueDate, version]);

  const navItems = useMemo(() => {
    const reportParams = isReport ? fullParams : {};
    return [
      {
        key: 'cross-table',
        label: t('view.crossTable'),
        to: new UriTemplate(
          `${path.visualValidationCrossTable}{?groupDescriptionRef,valueDate,version}`,
        ).expand(fullParams),
      },
      {
        key: 'time-series',
        label: t('view.timeSeries'),
        to: new UriTemplate(
          `${path.visualValidationTimeSeries}{?groupDescriptionRef}`,
        ).expand({ groupDescriptionRef }),
      },
      {
        key: 'comparison',
        label: t('view.comparison'),
        to: new UriTemplate(
          `${path.visualValidationComparison}{?groupDescriptionRef}`,
        ).expand({ groupDescriptionRef }),
      },
      {
        key: 'time-series-comparison',
        label: t('view.timeSeriesComparison'),
        to: new UriTemplate(
          `${path.visualValidationTimeSeriesComparison}{?groupDescriptionRef}`,
        ).expand({ groupDescriptionRef }),
      },
      {
        key: 'report',
        label: t('view.report'),
        to: new UriTemplate(
          `${path.visualValidationReport}{?groupDescriptionRef,valueDate,version}`,
        ).expand(reportParams),
      },
    ];
  }, [groupDescriptionRef, fullParams, isReport]);

  return <ButtonGroupNav items={navItems} />;
};
